import { useState, type ReactNode } from 'react';
import type { DateRange } from 'react-day-picker';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Calendar } from '@/components/atoms/calendar';
import { Label } from '@/components/atoms/label';
import { ScrollArea } from '@/components/atoms/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn } from '@/lib/utils';

interface EntityListAdvancedFilterTriggerProps {
  activeCount?: number;
  onClick: () => void;
  ariaLabel?: string;
}

export function EntityListAdvancedFilterTrigger({
  activeCount = 0,
  onClick,
  ariaLabel = 'Open filters',
}: EntityListAdvancedFilterTriggerProps) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn('relative h-8 gap-1.5 px-2.5', activeCount > 0 && 'border-primary')}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      <SlidersHorizontal className="h-3.5 w-3.5" />
      {activeCount > 0 ? (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
          {activeCount}
        </span>
      ) : null}
    </Button>
  );
}

interface EntityListDateAdvancedFilterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  range: DateRange | undefined;
  onApply: (range: DateRange | undefined) => void;
  onClear: () => void;
  title?: string;
  label?: string;
}

interface EntityListAdvancedFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeCount?: number;
  title?: string;
  children: ReactNode;
  onReset: () => void;
  resetDisabled?: boolean;
  onApply?: () => void;
}

export function EntityListAdvancedFiltersSheet({
  open,
  onOpenChange,
  activeCount = 0,
  title = 'Filters',
  children,
  onReset,
  resetDisabled,
  onApply,
}: EntityListAdvancedFiltersSheetProps) {
  const isDesktop = useIsDesktop();

  return (
    <>
      <EntityListAdvancedFilterTrigger
        activeCount={activeCount}
        onClick={() => onOpenChange(true)}
      />
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side={isDesktop ? 'right' : 'bottom'}
          className={cn('flex flex-col p-0', !isDesktop && 'max-h-[85vh]')}
        >
          <SheetHeader className="px-6 pb-2 pt-6">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          <ScrollArea className="flex-1 px-6">
            <div className="space-y-4 pb-4">{children}</div>
          </ScrollArea>
          <SheetFooter className="flex-row gap-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={resetDisabled ?? activeCount === 0}
              onClick={onReset}
            >
              <X className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={() => {
                onApply?.();
                onOpenChange(false);
              }}
            >
              Apply
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

export function EntityListDateAdvancedFilter({
  open,
  onOpenChange,
  range,
  onApply,
  onClear,
  title = 'Filters',
  label = 'Created date',
}: EntityListDateAdvancedFilterProps) {
  const isDesktop = useIsDesktop();
  const [draft, setDraft] = useState<DateRange | undefined>(range);
  const hasActiveDate = !!(range?.from || range?.to);
  const hasDraftDate = !!(draft?.from || draft?.to);

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) setDraft(range);
    onOpenChange(nextOpen);
  }

  function handleReset() {
    setDraft(undefined);
    onClear();
    onOpenChange(false);
  }

  function handleApply() {
    onApply(draft);
    onOpenChange(false);
  }

  return (
    <>
      <EntityListAdvancedFilterTrigger
        activeCount={hasActiveDate ? 1 : 0}
        onClick={() => handleOpenChange(true)}
      />
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side={isDesktop ? 'right' : 'bottom'}
          className={cn('flex flex-col p-0', !isDesktop && 'max-h-[85vh]')}
        >
          <SheetHeader className="px-6 pb-2 pt-6">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          <ScrollArea className="flex-1 px-6">
            <div className="space-y-3 pb-4">
              <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
              <div className="rounded-md border">
                <Calendar
                  mode="range"
                  selected={draft}
                  onSelect={setDraft}
                  numberOfMonths={1}
                  initialFocus
                />
              </div>
            </div>
          </ScrollArea>
          <SheetFooter className="flex-row gap-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={!hasActiveDate && !hasDraftDate}
              onClick={handleReset}
            >
              <X className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button type="button" className="flex-1" onClick={handleApply}>
              Apply
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
